import { InformationButton } from "@components/buttons/information";
import {
  SHAvatar,
  SHBox,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import { EssentialStarSVG } from "@components/svgs";
import { FeatureAnalysisRowDTO } from "@models/platform-analysis/entities/steps/feature";
import { FeatureAnalysisRowLevel } from "@models/platform-analysis/enums/feature/featureAnalysisRowLevel";
import { BusinessMetricCategoriesConfigs } from "@pages/platform-analysis-v2/_id/steps/business-metric/selection/configs";
import { memo } from "react";
import {
  RowTitleRenderConfigs,
  TableVirtualizationConfigs,
} from "../../../../configs";

type RenderedProductRowTitleCellProps = {
  rowData: FeatureAnalysisRowDTO;
};

export const RenderedProductRowTitleCell = memo(
  ({ rowData }: RenderedProductRowTitleCellProps) => {
    const {
      businessMetricTypeId,
      name = "",
      longestDataName,
      level,
      iconUrl,
      description,
      isEssential,
    } = rowData || {};
    const isFeatureLevel = level === FeatureAnalysisRowLevel.Feature;
    const { titleProps } = RowTitleRenderConfigs[level] || {};

    // #region Renderers
    const renderDescription = () => {
      if (!description) return null;

      return (
        <SHBox className="FeatureAnalysis-row-description">
          <InformationButton content={description} />
        </SHBox>
      );
    };

    const renderEssentialStar = () => {
      if (!isFeatureLevel || !isEssential) return null;

      return <EssentialStarSVG />;
    };

    const renderIcon = () => {
      if (iconUrl) {
        return (
          <SHAvatar src={iconUrl} width={22} height={22} isCircular={false} />
        );
      }

      if (!businessMetricTypeId) return null;

      return (
        BusinessMetricCategoriesConfigs.find(
          (config) => config.typeId === businessMetricTypeId,
        )?.icon || null
      );
    };
    // #endregion Renderers

    return (
      <SHStack
        width={"100%"}
        height={"100%"}
        justifyContent={"center"}
        alignItems={"center"}
        position={"relative"}
      >
        <SHStack
          width={"100%"}
          justifyContent={"space-between"}
          alignItems={"center"}
          direction={"row"}
          gap={1}
          position={"absolute"}
          top={0}
          left={0}
          height={"100%"}
        >
          <SHStack direction={"row"} alignItems={"center"} gap={"5px"}>
            {renderIcon()}
            <SHTypography {...titleProps} lineHeight={1.2}>
              {name}
              {businessMetricTypeId && (
                <>
                  {" "}
                  {rowData.totalFeaturesSelected}/{rowData.totalFeatures}
                </>
              )}
            </SHTypography>
          </SHStack>

          <SHStack
            direction={"row"}
            height={"100%"}
            justifyContent={"center"}
            alignItems={"center"}
            gap={0}
          >
            {renderEssentialStar()}
            {renderDescription()}
          </SHStack>
        </SHStack>

        <SHStack
          width={"100%"}
          justifyContent={"space-between"}
          alignItems={"center"}
          direction={"row"}
          visibility={"hidden"}
          sx={{ pointerEvents: "none" }}
        >
          <SHStack direction={"row"} alignItems={"center"} gap={"5px"}>
            <SHTypography
              lineHeight={1}
              sx={{
                width:
                  longestDataName !== name
                    ? TableVirtualizationConfigs.DefaultColumnWidth
                    : "100%",
                paddingX: "10px",
              }}
            >
              {longestDataName}
            </SHTypography>
          </SHStack>
        </SHStack>
      </SHStack>
    );
  },
);
